import { memo } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { Box, TextField, Typography } from '@material-ui/core';
import AddRepresentativesAgencyRenderList from '../AddRepresentativesFormListAgencyRenderOption/AddRepresentativesFormListAgencyRenderOption';
import {
  AddRepresentativesFormListAgencyOptionContainer,
  AddRepresentativesFormListAgencyOptionFetching,
} from './AddRepresentativesFormListAgencyTypeField.styles';
import { useAddRepresentativesGetAgencyOptions } from '../../hooks/useAddRepresentativesGetAgencyOptions';
import { IAgenciesAttributes } from 'shared/interfaces/IRepresentatives';
import { ErrorIcon } from '@room-match/shared-ui-components';

type AddRepresentativesFormListAgencyTypeFieldProps = {
  country: string;
  handleOnChange: (value: IAgenciesAttributes | null) => void;
};

const AddRepresentativesFormListAgencyTypeField = ({
  country,
  handleOnChange,
}: AddRepresentativesFormListAgencyTypeFieldProps) => {
  const {
    agency_options,
    isAgencyLoading,
    handleListScroll,
    handleAgencyInputChangeCB,
    isError,
    isFetchingAgencyNextPage,
  } = useAddRepresentativesGetAgencyOptions(country);

  return (
    <Autocomplete
      options={agency_options || []} // The agency option after fetching
      noOptionsText={
        // Display the error UI if the fetching is failed
        isError ? (
          <Box display={'flex'} alignItems={'center'} gridGap={10}>
            <ErrorIcon />
            <Typography style={{ fontSize: 14, color: 'unset' }}>
              Something went wrong while retrieving the agencies.
            </Typography>
          </Box>
        ) : undefined
      }
      loading={isAgencyLoading && agency_options?.length === 0} // will only show the UI loading if agency fetching loading and no options
      onInputChange={(e, newInputValue) => {
        /*  
          The e.type prevent the onInputChange to run when the onChange is executed
          This prevents the invalidation of the agency pagination and prevents the api call after selecting on onChange,
          the agency pagination should be only be invalidated if the user type
          not when the onChange is executed. This can be seen in representativeService 
        */
        if (e.type === 'change') handleAgencyInputChangeCB(newInputValue);
      }}
      onChange={(_, value) => {
        handleOnChange(value);
      }}
      // Its possible agencies doesnt have a full name only a name
      getOptionLabel={(option) => option?.full_name || option?.name || ''}
      // This highlights the selected agency, so this line below and above should be in sync!
      getOptionSelected={(option, value) =>
        (option?.full_name && option.full_name === value?.full_name) ||
        (option?.name && option.name === value?.name) ||
        option?.id === value?.id
      }
      // The custom render of option items
      renderOption={(option) => (
        <AddRepresentativesFormListAgencyOptionContainer>
          <AddRepresentativesAgencyRenderList option={option} />
          {isFetchingAgencyNextPage && option?.id === agency_options?.[agency_options.length - 1]?.id && (
            <AddRepresentativesFormListAgencyOptionFetching size={16} />
          )}
        </AddRepresentativesFormListAgencyOptionContainer>
      )}
      renderInput={(params: { inputProps: any }) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Type / Search your representation here"
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleListScroll,
      }}
    />
  );
};

export default memo(AddRepresentativesFormListAgencyTypeField);
